import type { Dispatch, SetStateAction } from 'react';
import { useGlobalAppStateStore } from '../../../../../no-code/stores/app/GlobalAppStateStore';
import type { NavItem, NavigationItem } from '../../../types';
import { getActiveNavItem, getIsPathActive } from '../../SideNavigation/helpers';
import { ActionItem } from './ActionItem';
import { GroupItem } from './GroupItem';
import { UrlItem } from './UrlItem';
import { UserItem } from './UserItem';

type Props = {
  navItem: NavigationItem;
  activePathnames: string[];
  className?: string;
  openGroupId?: string | null;
  setOpenGroupId?: Dispatch<SetStateAction<string | null>>;
};

export function ItemRenderer({
  navItem,
  activePathnames,
  className,
  openGroupId,
  setOpenGroupId,
}: Props) {
  const isNavigationDrawerOpen = useGlobalAppStateStore().hooks.useIsNavigationDrawerOpen();

  if (navItem.type === 'GROUP') {
    const activeNavItem = getActiveNavItem({
      navItems: navItem.items,
      activePathnames,
    });
    /**
     *  safe to assume that the items are all URL items for now
     *  since we do not have support for 2-level or more nested Nav items
     */
    const isActive = navItem.items.some((subItem: NavItem) =>
      getIsPathActive({ activePathnames, path: subItem.url }),
    );

    return (
      <GroupItem
        activeNavItem={activeNavItem}
        className={className}
        isActive={isActive}
        // To collapse the navgroup when the drawer is closed
        // and reopened again if its subitem is not selected
        key={isNavigationDrawerOpen ? '1' : '0'}
        navItem={navItem}
        openGroupId={openGroupId}
        setOpenGroupId={setOpenGroupId}
      />
    );
  }

  if (navItem.type === 'USER_MENU') {
    return <UserItem navItem={navItem} />;
  }

  // For Notification Item
  if (navItem.type === 'ACTION') {
    return (
      <ActionItem
        className={className}
        isActive={false}
        navItem={navItem}
        onClick={navItem.onClick as () => void}
      />
    );
  }

  // No need to render LABEL or CUSTOM items in drawer Navigation
  if (
    navItem.type === 'LABEL' ||
    navItem.type === 'CUSTOM' ||
    navItem.type === 'APPLICATION_PAGE' ||
    navItem.type === 'COLOR_SCHEME_SWITCHER' ||
    navItem.type === 'LANGUAGE_SWITCHER' ||
    navItem.type === 'BUTTON'
  ) {
    return null;
  }

  const isActive = getIsPathActive({ activePathnames, path: navItem.url });

  return (
    <UrlItem
      className={className}
      isActive={isActive}
      navItem={navItem}
      readonly={navItem.readonly}
    />
  );
}
